import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, Link } from "react-router-dom";
import {
  Menu,
  X,
  Sparkles,
  Clock,
  TrendingUp,
  FileText,
  Monitor,
  RotateCw,
  Users,
  Network,
  Building2,
  ChevronDown,
  GraduationCap,
  Briefcase,
  Play,
} from "lucide-react";
import { buttonHoverVariants } from "../../utils/landingAnimations";

const PublicHeader = ({ minimal = false } = {}) => {
  const navigate = useNavigate();

  const [isMenuOpen, setIsMenuOpen] = useState(() => {
    // Synchronous state check to prevent flash - no intermediate render
    try {
      const shouldOpenMenu = sessionStorage.getItem("menuReturnState");
      const shouldExpandFeatures = sessionStorage.getItem(
        "featuresReturnState",
      );

      // If we have return state, this is back navigation - keep states
      // If no return state, this is forward navigation - clear any stale state
      if (shouldOpenMenu !== "true" && shouldExpandFeatures !== "true") {
        // Clear any stale states on forward navigation
        sessionStorage.removeItem("menuReturnState");
        sessionStorage.removeItem("featuresReturnState");
      }

      return shouldOpenMenu === "true";
    } catch (error) {
      console.warn("Failed to read menu state:", error);
      return false;
    }
  });

  const [isMobileFeaturesOpen, setIsMobileFeaturesOpen] = useState(() => {
    // Synchronous state check for features dropdown
    try {
      const shouldExpandFeatures = sessionStorage.getItem(
        "featuresReturnState",
      );
      return shouldExpandFeatures === "true";
    } catch (error) {
      console.warn("Failed to read features state:", error);
      return false;
    }
  });

  const [isMobileSolutionsOpen, setIsMobileSolutionsOpen] = useState(false);

  const [isFeaturesHovered, setIsFeaturesHovered] = useState(false);
  const featuresHoverTimeoutRef = useRef(null);
  const [isSolutionsHovered, setIsSolutionsHovered] = useState(false);
  const solutionsHoverTimeoutRef = useRef(null);

  // Clear sessionStorage after use (only if states were restored)
  useEffect(() => {
    // Only clear if we actually restored states (back navigation)
    const hadMenuState = sessionStorage.getItem("menuReturnState") === "true";
    const hadFeaturesState =
      sessionStorage.getItem("featuresReturnState") === "true";

    if (hadMenuState || hadFeaturesState) {
      try {
        sessionStorage.removeItem("menuReturnState");
        sessionStorage.removeItem("featuresReturnState");
      } catch (error) {
        console.warn("Failed to clear menu states:", error);
      }
    }
  }, []);

  // Handle back/forward navigation
  useEffect(() => {
    const handlePopState = (event) => {
      // On back/forward navigation, check if we should restore menu state
      // We need to check sessionStorage again since it gets cleared after initial load
      try {
        const shouldOpenMenu = sessionStorage.getItem("menuReturnState");
        const shouldExpandFeatures = sessionStorage.getItem(
          "featuresReturnState",
        );

        if (shouldOpenMenu === "true") {
          setIsMenuOpen(true);
        } else {
          setIsMenuOpen(false);
        }

        if (shouldExpandFeatures === "true") {
          setIsMobileFeaturesOpen(true);
        } else {
          setIsMobileFeaturesOpen(false);
        }

        // Clear states after use
        sessionStorage.removeItem("menuReturnState");
        sessionStorage.removeItem("featuresReturnState");
      } catch (error) {
        console.warn("Failed to handle popstate:", error);
      }
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  // Handle all menu navigation
  const handleMenuNavigation = (path, isFeaturesNavigation = false) => {
    window.scrollTo(0, 0);
    // Close menu before navigation
    setIsMenuOpen(false);
    setIsMobileFeaturesOpen(false);
    setIsMobileSolutionsOpen(false);

    // Set return state AFTER navigation completes, not before
    // Use a timeout to ensure navigation has started
    setTimeout(() => {
      try {
        sessionStorage.setItem("menuReturnState", "false");

        // If this is features navigation, also set features return state
        if (isFeaturesNavigation) {
          sessionStorage.setItem("featuresReturnState", "false");
        } else {
          // Clear features state for non-features navigation
          sessionStorage.removeItem("featuresReturnState");
        }
      } catch (error) {
        console.warn("Failed to set menu return state:", error);
      }
    }, 100);

    // Navigate normally
    navigate(path);
  };

  const handleFeaturesMouseEnter = () => {
    if (featuresHoverTimeoutRef.current) {
      clearTimeout(featuresHoverTimeoutRef.current);
    }
    if (solutionsHoverTimeoutRef.current) {
      clearTimeout(solutionsHoverTimeoutRef.current);
    }
    setIsSolutionsHovered(false);
    setIsFeaturesHovered(true);
  };

  const handleFeaturesMouseLeave = () => {
    featuresHoverTimeoutRef.current = setTimeout(() => {
      setIsFeaturesHovered(false);
    }, 200);
  };

  const handleSolutionsMouseEnter = () => {
    if (solutionsHoverTimeoutRef.current) {
      clearTimeout(solutionsHoverTimeoutRef.current);
    }
    if (featuresHoverTimeoutRef.current) {
      clearTimeout(featuresHoverTimeoutRef.current);
    }
    setIsFeaturesHovered(false);
    setIsSolutionsHovered(true);
  };

  const handleSolutionsMouseLeave = () => {
    solutionsHoverTimeoutRef.current = setTimeout(() => {
      setIsSolutionsHovered(false);
    }, 200);
  };

  const features = [
    {
      icon: Sparkles,
      title: "Ask TeamGrid",
      desc: "Turn questions into actionable insights about productivity.",
      path: "/features/ai-powered-insights",
    },
    {
      icon: Clock,
      title: "Automatic Time Tracking",
      desc: "Silent, precise tracking across all apps and projects.",
      path: "/features/automatic-time-tracking",
    },
    {
      icon: TrendingUp,
      title: "Productivity Scoring",
      desc: "Real-time productivity metrics for individuals and teams.",
      path: "/features/productivity-scoring",
    },
    {
      icon: Users,
      title: "Team Management",
      desc: "Roles, permissions, and organizational hierarchy.",
      path: "/features/team-management",
    },
    {
      icon: Building2,
      title: "Org Intelligence",
      desc: "AI-driven organization-wide performance insights.",
      path: "/features/org-intelligence",
    },
    {
      icon: FileText,
      title: "AI Work Summaries",
      desc: "Auto-generated daily and monthly work reports.",
      path: "/features/ai-work-summaries",
    },
    {
      icon: Monitor,
      title: "App & Website Tracking",
      desc: "Monitor application and website usage patterns.",
      path: "/features/app-website-tracking",
    },
    {
      icon: RotateCw,
      title: "Hourly Breakdown",
      desc: "Detailed hour-by-hour activity analysis.",
      path: "/features/hourly-breakdown",
    },
    {
      icon: Network,
      title: "Project Tracking",
      desc: "Monitor project progress and team allocations.",
      path: "/features/project-tracking",
    },
  ];

  const solutions = [
    {
      icon: Users,
      title: "Remote Teams",
      desc: "Clarity into remote team delivery",
      path: "/solutions/remote-team-productivity",
    },
    {
      icon: GraduationCap,
      title: "EdTech Productivity",
      desc: "Clarity across EdTech team workflows",
      path: "/solutions/edtech-productivity-software",
    },
    {
      icon: Users,
      title: "HR & Staffing Firms",
      desc: "Visibility into recruiter performance",
      path: "/solutions/hr-staffing-firms",
    },
    {
      icon: FileText,
      title: "Accounting Firms",
      desc: "Capture every billable hour",
      path: "/solutions/accounting-firm-productivity",
    },
    {
      icon: Building2,
      title: "Virtual Services",
      desc: "Visibility into support performance",
      path: "/solutions/virtual-support-services",
    },
    {
      icon: Briefcase,
      title: "Operations & Execution Teams",
      desc: "Clarity across operational workflows",
      path: "/solutions/operations-execution-teams",
    },
    {
      icon: Users,
      title: "BPO & Shared Services",
      desc: "Visibility across distributed operations",
      path: "/solutions/bpo-shared-services",
    },
    {
      icon: Briefcase,
      title: "Contract Teams",
      desc: "Clarity across contract delivery",
      path: "/solutions/contract-freelance-teams",
    },
    {
      icon: Users,
      title: "Client-Driven Teams",
      desc: "Balance client work and productivity",
      path: "/solutions/client-driven-work",
    },
    {
      icon: Network,
      title: "Project-Based Teams",
      desc: "Clarity across project execution",
      path: "/solutions/project-based-teams",
    },
  ];

  return (
    <nav className="public-header-nav fixed top-0 w-full bg-black backdrop-blur-[10px] z-50 border-b border-black">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16 sm:h-18 md:h-20 relative">
          {/* Logo - Left - go to main landing, reset alternate view if active, scroll hero to top */}
          <div
            className="flex-shrink-0 flex items-center cursor-pointer"
            onClick={() => {
              const hero = document.getElementById("landing-hero");
              if (hero) hero.scrollIntoView({ block: "start", behavior: "smooth" });
              else window.scrollTo(0, 0);
              
              setIsMenuOpen(false);
              setIsMobileFeaturesOpen(false);
              setIsMobileSolutionsOpen(false);
              
              navigate("/", { state: { showMainLanding: true }, replace: true });
            }}
          >
            <img
              src="/logo.svg"
              alt="TeamGrid Logo"
              width={185}
              height={44}
              className="flex-shrink-0 w-[140px] sm:w-[160px] md:w-[185px] h-auto"
              style={{ objectFit: "contain" }}
            />
          </div>

          {/* Desktop Navigation - Center Links */}
          <div className={`${minimal ? "hidden" : "hidden md:flex"} items-center space-x-3 lg:space-x-4 xl:space-x-6 ml-6 lg:ml-10 xl:ml-14 mr-auto`}>
            <div
              className="relative"
              onMouseEnter={handleFeaturesMouseEnter}
              onMouseLeave={handleFeaturesMouseLeave}
            >
              <button
                className="text-silver-chalice transition-colors text-[14px] xl:text-[15px] font-medium cursor-pointer flex items-center gap-1 bg-transparent border-none"
                style={{ color: "#A0A0A0" }}
              >
                Features
                <ChevronDown
                  className={`w-4 h-4 transition-transform duration-200 ${
                    isFeaturesHovered ? "rotate-180" : "rotate-0"
                  }`}
                />
              </button>

              {/* Features Dropdown */}
              <AnimatePresence>
                {isFeaturesHovered && (
                  <>
                    {/* Background Blur Overlay - Only blurs page content, not header or dropdown */}
                    <motion.div
                      className="fixed left-0 right-0 bottom-0 bg-black/50 backdrop-blur-2xl"
                      style={{
                        top: "64px",
                        zIndex: 49,
                        pointerEvents: "none",
                      }}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
                    />
                    <div
                      className="fixed left-0 right-0 pt-2 w-full z-50"
                      style={{ top: "64px" }}
                      onMouseEnter={handleFeaturesMouseEnter}
                      onMouseLeave={handleFeaturesMouseLeave}
                    >
                      <div
                        className="absolute top-0 left-0 right-0 w-full h-2 -mt-2 pointer-events-auto"
                        onMouseEnter={handleFeaturesMouseEnter}
                        onMouseLeave={handleFeaturesMouseLeave}
                      ></div>
                      <motion.div
                        key="features-dropdown"
                        initial={{ opacity: 0, y: -8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -8 }}
                        transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
                        className="w-full"
                        onMouseEnter={handleFeaturesMouseEnter}
                        onMouseLeave={handleFeaturesMouseLeave}
                      >
                        <div
                          className="w-full border-x-0 border-t-0 border-b border-gray-200"
                          style={{
                            willChange: "opacity",
                            background: "#FFFFFF",
                          }}
                          onMouseEnter={handleFeaturesMouseEnter}
                          onMouseLeave={handleFeaturesMouseLeave}
                        >
                          <div
                            className="px-4 sm:px-6 lg:px-8 pt-8 pb-0"
                            style={{ background: "#FFFFFF" }}
                          >
                            {/* Header */}
                            <div className="mb-8">
                              <h2 className="text-[#232323] text-xl font-medium mb-3">
                                Explore the features that help your team succeed
                              </h2>
                              <div className="w-full h-px bg-gray-200" />
                            </div>

                            {/* Features Grid */}
                            <div className="space-y-6">
                              {/* First Row - 5 features */}
                              <div className="grid grid-cols-5 gap-4">
                                {features.slice(0, 5).map((feature, idx) => (
                                  <div
                                    key={idx}
                                    onClick={() => {
                                      if (feature.path) {
                                        setIsFeaturesHovered(false);
                                        window.scrollTo(0, 0);
                                        navigate(feature.path);
                                      }
                                    }}
                                    className="text-center cursor-pointer transition-opacity hover:opacity-80"
                                  >
                                    <div className="flex justify-center mb-3">
                                      <feature.icon className="w-6 h-6 text-[#232323]" />
                                    </div>
                                    <h3 className="text-[#232323] font-semibold text-sm mb-2">
                                      {feature.title}
                                    </h3>
                                    <p className="text-gray-600 text-xs leading-relaxed">
                                      {feature.desc}
                                    </p>
                                  </div>
                                ))}
                              </div>

                              {/* Second Row - 4 features + Watch Demo button */}
                              <div className="grid grid-cols-5 gap-4 items-center">
                                {features.slice(5, 9).map((feature, idx) => (
                                  <div
                                    key={idx + 5}
                                    onClick={() => {
                                      if (feature.path) {
                                        setIsFeaturesHovered(false);
                                        window.scrollTo(0, 0);
                                        navigate(feature.path);
                                      }
                                    }}
                                    className="text-center cursor-pointer transition-opacity hover:opacity-80"
                                  >
                                    <div className="flex justify-center mb-3">
                                      <feature.icon className="w-6 h-6 text-[#232323]" />
                                    </div>
                                    <h3 className="text-[#232323] font-semibold text-sm mb-2">
                                      {feature.title}
                                    </h3>
                                    <p className="text-gray-600 text-xs leading-relaxed">
                                      {feature.desc}
                                    </p>
                                  </div>
                                ))}
                                {/* Watch Demo Button */}
                                <div className="flex items-center justify-center h-full">
                                  <a
                                    // href="/book-demo"
                                    href="https://youtu.be/H2qiGOXlwG8?si=NPwGnFTy-f1-rVMw"
                                    target="_blank"
                                    className="flex items-center gap-2 bg-[#232323] border border-[#232323] hover:border-[#28B4AE]/50 font-semibold text-[16px] leading-[24px] px-4 py-2.5 rounded-lg transition-colors !text-white"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                    }}
                                  >
                                    <Play className="w-4 h-4 flex-shrink-0 text-[#28B4AE]" />
                                    <span className="!text-white">
                                      Watch Demo
                                    </span>
                                  </a>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Bottom Section - dark footer */}
                          <div className="mt-6 py-4 px-4 sm:px-6 lg:px-8 flex items-center justify-between !bg-black">
                            <span className="text-[#00D1C1] text-sm font-medium">
                              Ready to Understand Your Team?
                            </span>
                            <div className="flex items-center gap-4">
                              <button
                                type="button"
                                className="text-[#00D1C1] hover:opacity-90 text-sm transition-colors bg-transparent border-none cursor-pointer p-0"
                                onClick={(e) => {
                                  e.preventDefault();
                                  setIsFeaturesHovered(false);
                                  window.open("/book-demo", "_blank");
                                }}
                              >
                                Book Demo
                              </button>
                              <button
                                type="button"
                                className="text-[#00D1C1] hover:opacity-90 text-sm transition-colors bg-transparent border-none cursor-pointer p-0"
                                onClick={(e) => {
                                  e.preventDefault();
                                  setIsFeaturesHovered(false);
                                  navigate("/register");
                                }}
                              >
                                {/* Free 5-Day Trial */}
                                Register
                              </button>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    </div>
                  </>
                )}
              </AnimatePresence>
            </div>
            <div
              className="relative"
              onMouseEnter={handleSolutionsMouseEnter}
              onMouseLeave={handleSolutionsMouseLeave}
            >
              <span
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setIsSolutionsHovered((prev) => !prev);
                  }
                }}
                className="text-silver-chalice transition-colors text-[14px] xl:text-[15px] font-medium cursor-pointer flex items-center gap-1"
                style={{ color: "#A0A0A0" }}
              >
                Solutions
                <ChevronDown
                  className={`w-4 h-4 transition-transform duration-200 ${
                    isSolutionsHovered ? "rotate-180" : "rotate-0"
                  }`}
                />
              </span>

              {/* Solutions Dropdown */}
              <AnimatePresence>
                {isSolutionsHovered && (
                  <>
                    {/* Background Blur Overlay - Only blurs page content, not header or dropdown */}
                    <motion.div
                      className="fixed left-0 right-0 bottom-0 bg-black/50 backdrop-blur-2xl"
                      style={{
                        top: "64px",
                        zIndex: 49,
                        pointerEvents: "none",
                      }}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
                    />
                    <div
                      className="fixed left-0 right-0 pt-2 w-full z-50"
                      style={{ top: "64px" }}
                      onMouseEnter={handleSolutionsMouseEnter}
                      onMouseLeave={handleSolutionsMouseLeave}
                    >
                      <div
                        className="absolute top-0 left-0 right-0 w-full h-2 -mt-2 pointer-events-auto"
                        onMouseEnter={handleSolutionsMouseEnter}
                        onMouseLeave={handleSolutionsMouseLeave}
                      ></div>
                      <motion.div
                        key="solutions-dropdown"
                        initial={{ opacity: 0, y: -8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -8 }}
                        transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
                        className="w-full"
                        onMouseEnter={handleSolutionsMouseEnter}
                        onMouseLeave={handleSolutionsMouseLeave}
                      >
                        <div
                          className="w-full border-x-0 border-t-0 border-b border-gray-200"
                          style={{
                            willChange: "opacity",
                            background: "#FFFFFF",
                          }}
                          onMouseEnter={handleSolutionsMouseEnter}
                          onMouseLeave={handleSolutionsMouseLeave}
                        >
                          <div
                            className="px-4 sm:px-6 lg:px-8 pt-8 pb-0"
                            style={{ background: "#FFFFFF" }}
                          >
                            {/* Header */}
                            <div className="mb-8">
                              <h2 className="text-[#232323] text-xl font-medium mb-3">
                                Explore the solutions that help your team
                                succeed
                              </h2>
                              <div className="w-full h-px bg-gray-200"></div>
                            </div>

                            {/* Solutions Grid */}
                            <div className="space-y-6">
                              {/* First Row - 5 solutions */}
                              <div className="grid grid-cols-5 gap-4">
                                {solutions.slice(0, 5).map((solution, idx) => (
                                  <div
                                    key={idx}
                                    onClick={() => {
                                      if (solution.path) {
                                        window.scrollTo(0, 0);
                                        setIsSolutionsHovered(false);
                                        navigate(solution.path);
                                      }
                                    }}
                                    className={`text-center cursor-pointer transition-opacity hover:opacity-80 ${
                                      solution.path ? "" : ""
                                    }`}
                                  >
                                    <div className="flex justify-center mb-3">
                                      <solution.icon className="w-6 h-6 text-[#232323]" />
                                    </div>
                                    <h3 className="text-[#232323] font-semibold text-sm mb-2">
                                      {solution.title}
                                    </h3>
                                    <p className="text-gray-600 text-xs leading-relaxed">
                                      {solution.desc}
                                    </p>
                                  </div>
                                ))}
                              </div>

                              {/* Second Row - 5 solutions */}
                              <div className="grid grid-cols-5 gap-4">
                                {solutions.slice(5, 10).map((solution, idx) => (
                                  <div
                                    key={idx + 5}
                                    onClick={() => {
                                      if (solution.path) {
                                        window.scrollTo(0, 0);
                                        setIsSolutionsHovered(false);
                                        navigate(solution.path);
                                      }
                                    }}
                                    className={`text-center cursor-pointer transition-opacity hover:opacity-80 ${
                                      solution.path ? "" : ""
                                    }`}
                                  >
                                    <div className="flex justify-center mb-3">
                                      <solution.icon className="w-6 h-6 text-[#232323]" />
                                    </div>
                                    <h3 className="text-[#232323] font-semibold text-sm mb-2">
                                      {solution.title}
                                    </h3>
                                    <p className="text-gray-600 text-xs leading-relaxed">
                                      {solution.desc}
                                    </p>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>

                          {/* Bottom Section - dark footer (match Features dropdown) */}
                          <div className="mt-6 py-4 px-4 sm:px-6 lg:px-8 flex items-center justify-between !bg-black">
                            <span className="text-[#00D1C1] text-sm font-medium">
                              Ready to Understand Your Team?
                            </span>
                            <div className="flex items-center gap-4">
                              <button
                                type="button"
                                className="text-[#00D1C1] hover:opacity-90 text-sm transition-colors bg-transparent border-none cursor-pointer p-0"
                                onClick={(e) => {
                                  e.preventDefault();
                                  setIsSolutionsHovered(false);
                                  window.open("/book-demo", "_blank");
                                }}
                              >
                                Book Demo
                              </button>
                              <button
                                type="button"
                                className="text-[#00D1C1] hover:opacity-90 text-sm transition-colors bg-transparent border-none cursor-pointer p-0"
                                onClick={(e) => {
                                  e.preventDefault();
                                  setIsSolutionsHovered(false);
                                  navigate("/register");
                                }}
                              >
                                {/* Free 5-Day Trial */}
                                Register
                              </button>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    </div>
                  </>
                )}
              </AnimatePresence>
            </div>
            <Link
              to="/#pricing"
              onClick={(e) => {
                if (window.location.pathname === "/") {
                  e.preventDefault();
                  document.getElementById("pricing")?.scrollIntoView({
                    behavior: "smooth",
                    block: "start",
                  });
                }
              }}
              className="text-silver-chalice transition-colors text-[14px] xl:text-[15px] font-medium"
              style={{ color: "#A0A0A0" }}
            >
              Pricing
            </Link>
            <a
              href="/contact-us"
              className="text-silver-chalice transition-colors text-[14px] xl:text-[15px] font-medium"
              style={{ color: "#A0A0A0" }}
            >
              Contact Us
            </a>
            <Link
              to="/download/all"
              className="text-silver-chalice transition-colors text-[14px] xl:text-[15px] font-medium"
              style={{ color: "#A0A0A0" }}
            >
              Download
            </Link>
          </div>

          {/* Desktop Navigation - Right Side Actions */}
          <div className={`${minimal ? "flex ml-auto gap-3 sm:gap-5" : "hidden md:flex gap-5"} items-center flex-shrink-0`}>
            {!minimal && (
            <Link
              to="/resource-optimizer"
              className="text-[13.5px] xl:text-[14px] font-semibold whitespace-nowrap flex-shrink-0"
              style={{
                color: "#FFFFFF",
                display: "inline-flex",
                alignItems: "center",
                gap: "8px",
                padding: "7px 16px 7px 13px",
                borderRadius: "999px",
                border: "1px solid rgba(0,209,193,0.55)",
                background:
                  "linear-gradient(135deg, rgba(0,209,193,0.10) 0%, rgba(10,15,18,0.55) 100%)",
                boxShadow:
                  "inset 0 1px 0 rgba(255,255,255,0.06), 0 0 22px rgba(0,209,193,0.16), 0 4px 12px rgba(0,0,0,0.35)",
                backdropFilter: "blur(8px)",
                WebkitBackdropFilter: "blur(8px)",
                letterSpacing: "0.005em",
                transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
                transform: "translateY(0)",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = "rgba(0,224,206,0.9)";
                e.currentTarget.style.background =
                  "linear-gradient(135deg, rgba(0,209,193,0.18) 0%, rgba(10,15,18,0.45) 100%)";
                e.currentTarget.style.boxShadow =
                  "inset 0 1px 0 rgba(255,255,255,0.10), 0 0 28px rgba(0,209,193,0.28), 0 6px 16px rgba(0,0,0,0.4)";
                e.currentTarget.style.transform = "translateY(-1px)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "rgba(0,209,193,0.55)";
                e.currentTarget.style.background =
                  "linear-gradient(135deg, rgba(0,209,193,0.10) 0%, rgba(10,15,18,0.55) 100%)";
                e.currentTarget.style.boxShadow =
                  "inset 0 1px 0 rgba(255,255,255,0.06), 0 0 22px rgba(0,209,193,0.16), 0 4px 12px rgba(0,0,0,0.35)";
                e.currentTarget.style.transform = "translateY(0)";
              }}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="#00D1C1"
                aria-hidden="true"
                style={{ filter: "drop-shadow(0 0 6px rgba(0,209,193,0.55))", flexShrink: 0 }}
              >
                <path d="M12 2l1.8 6.2L20 10l-6.2 1.8L12 18l-1.8-6.2L4 10l6.2-1.8L12 2z" />
                <path d="M19 14l.7 2.3L22 17l-2.3.7L19 20l-.7-2.3L16 17l2.3-.7L19 14z" fill="#00D1C1" opacity="0.7" />
              </svg>
              <span>Managed Services</span>
            </Link>
            )}
            {!minimal && (
            <a
              href="/login"
              className="flex items-center justify-center transition-colors text-[14px] xl:text-[15px] font-medium"
              style={{ color: "#A0A0A0" }}
              // style={{ color: "#00E0CE" }}
              onMouseEnter={(e) => (e.target.style.color = "#FFFFFF")}
              onMouseLeave={(e) => (e.target.style.color = "#A0A0A0")}
            >
              Sign In
            </a>
            )}
            <a
              href="/book-demo"
              target="_blank"
              className="flex items-center justify-center transition-colors text-[14px] xl:text-[15px] font-medium"
              style={{ color: "#00D1C1" }}
              onMouseEnter={(e) => (e.target.style.color = "#00E0CE")}
              onMouseLeave={(e) => (e.target.style.color = "#00D1C1")}
            >
              Demo
            </a>
            <motion.a
              href="/register"
              className="flex items-center justify-center font-semibold text-[14px] xl:text-[15px] rounded-lg px-5 py-2.5 whitespace-nowrap"
              style={{
                background:
                  "linear-gradient(108.58deg, #00D1C1 0%, #00A89B 100%)",
                color: "#000000",
              }}
              variants={buttonHoverVariants}
              whileHover="hover"
              whileTap="tap"
            >
              {/* Free 5-Day Trial */}
              Register
            </motion.a>
          </div>

          {/* Mobile menu button */}
          <div className={`${minimal ? "hidden" : "md:hidden"} flex-shrink-0 z-50`}>
            <button
              onClick={() => {
                const nextState = !isMenuOpen;
                setIsMenuOpen(nextState);
                if (!nextState) {
                  setIsMobileFeaturesOpen(false);
                  setIsMobileSolutionsOpen(false);
                }
              }}
              className="public-header-menu-btn text-white hover:text-white/80 transition-colors p-2 rounded-lg hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-white/20"
              aria-label="Toggle menu"
              type="button"
            >
              {isMenuOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="public-header-mobile-menu md:hidden py-4 border-t border-white/10"
          >
            <div className="flex flex-col space-y-4">
              <div
                className="relative"
                onMouseEnter={() => {
                  setIsMobileSolutionsOpen(false);
                  setIsMobileFeaturesOpen(true);
                }}
                onMouseLeave={() => setIsMobileFeaturesOpen(false)}
              >
                <div
                  className="flex items-center justify-between text-white/70 hover:text-white transition-colors cursor-pointer"
                  onClick={() => {
                    setIsMobileSolutionsOpen(false);
                    setIsMobileFeaturesOpen(!isMobileFeaturesOpen);
                  }}
                >
                  <span>Features</span>
                  <ChevronDown
                    color="currentColor"
                    className={`w-5 h-5 transition-transform duration-200 ${
                      isMobileFeaturesOpen ? "rotate-180 text-[#00D1C1]" : "rotate-0 text-white/70"
                    }`}
                  />
                </div>

                {/* Mobile Features Sub-cards */}
                <AnimatePresence>
                  {isMobileFeaturesOpen && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.2 }}
                      className="mt-2"
                    >
                      <div
                        className="grid grid-cols-1 gap-3 pl-4 pt-2 max-h-[60vh] overflow-y-auto pr-2"
                        style={{
                          scrollbarWidth: "thin",
                          scrollbarColor:
                            "rgba(255, 255, 255, 0.2) transparent",
                        }}
                      >
                        {features.map((feature, idx) => {
                          const IconComponent = feature.icon;
                          return (
                            <motion.div
                              key={idx}
                              initial={{ opacity: 0, x: -10 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{
                                duration: 0.2,
                                delay: idx * 0.03,
                              }}
                              className="relative group"
                              onClick={() => {
                                setIsMobileFeaturesOpen(false);
                                handleMenuNavigation(feature.path, true); // Mark as features navigation
                              }}
                            >
                              <div
                                className={`relative rounded-lg p-3 border ${feature.borderColor} bg-gradient-to-br from-white/5 to-white/0.8 backdrop-blur-[40px] cursor-pointer hover:border-opacity-60 transition-all duration-200`}
                              >
                                <div className="flex items-start gap-3">
                                  <div
                                    className={`w-10 h-10 rounded-lg ${feature.iconBg} flex items-center justify-center flex-shrink-0`}
                                  >
                                    <IconComponent
                                      className={`w-5 h-5 ${feature.iconColor}`}
                                    />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <h4 className="text-white font-semibold text-sm mb-1">
                                      {feature.title}
                                    </h4>
                                    <p className="text-gray-400 text-xs leading-relaxed">
                                      {feature.desc}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </motion.div>
                          );
                        })}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
              <div
                className="relative"
                onMouseEnter={() => {
                  setIsMobileFeaturesOpen(false);
                  setIsMobileSolutionsOpen(true);
                }}
                onMouseLeave={() => setIsMobileSolutionsOpen(false)}
              >
                <div
                  className="flex items-center justify-between text-white/70 hover:text-white transition-colors cursor-pointer"
                  onClick={() => {
                    setIsMobileFeaturesOpen(false);
                    setIsMobileSolutionsOpen(!isMobileSolutionsOpen);
                  }}
                >
                  <span>Solutions</span>
                  <ChevronDown
                    color="currentColor"
                    className={`w-5 h-5 transition-transform duration-200 ${
                      isMobileSolutionsOpen ? "rotate-180 text-[#00D1C1]" : "rotate-0 text-white/70"
                    }`}
                  />
                </div>

                {/* Mobile Solutions Sub-cards */}
                <AnimatePresence>
                  {isMobileSolutionsOpen && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.2 }}
                      className="mt-2"
                    >
                      <div
                        className="grid grid-cols-1 gap-3 pl-4 pt-2 max-h-[60vh] overflow-y-auto pr-2"
                        style={{
                          scrollbarWidth: "thin",
                          scrollbarColor:
                            "rgba(255, 255, 255, 0.2) transparent",
                        }}
                      >
                        {solutions.map((solution, idx) => {
                          const IconComponent = solution.icon;
                          return (
                            <motion.div
                              key={idx}
                              initial={{ opacity: 0, x: -10 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{
                                duration: 0.2,
                                delay: idx * 0.03,
                              }}
                              className="relative group"
                              onClick={() => {
                                setIsMobileSolutionsOpen(false);
                                handleMenuNavigation(solution.path, false);
                              }}
                            >
                              <div
                                className={`relative rounded-lg p-3 border ${solution.borderColor} bg-gradient-to-br from-white/5 to-white/0.8 backdrop-blur-[40px] cursor-pointer hover:border-opacity-60 transition-all duration-200`}
                              >
                                <div className="flex items-start gap-3">
                                  <div
                                    className={`w-10 h-10 rounded-lg ${solution.iconBg} flex items-center justify-center flex-shrink-0`}
                                  >
                                    <IconComponent
                                      className={`w-5 h-5 ${solution.iconColor}`}
                                    />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <h4 className="text-white font-semibold text-sm mb-1">
                                      {solution.title}
                                    </h4>
                                    <p className="text-gray-400 text-xs leading-relaxed">
                                      {solution.desc}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </motion.div>
                          );
                        })}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
              <Link
                to="/#pricing"
                onClick={(e) => {
                  setIsMenuOpen(false);
                  setIsMobileFeaturesOpen(false);
                  setIsMobileSolutionsOpen(false);
                  if (window.location.pathname === "/") {
                    e.preventDefault();
                    document.getElementById("pricing")?.scrollIntoView({
                      behavior: "smooth",
                      block: "start",
                    });
                  }
                }}
                className="text-white/70 hover:text-white transition-colors"
              >
                Pricing
              </Link>
              <a
                href="/contact-us"
                onClick={(e) => {
                  e.preventDefault();
                  handleMenuNavigation("/contact-us", false);
                }}
                className="text-white/70 hover:text-white transition-colors"
              >
                Contact Us
              </a>
              <Link
                to="/download/all"
                onClick={() => handleMenuNavigation("/download/all", false)}
                className="text-white/70 hover:text-white transition-colors"
              >
                Download
              </Link>
              <Link
                to="/resource-optimizer"
                onClick={() => handleMenuNavigation("/resource-optimizer", false)}
                className="transition-colors font-semibold"
                style={{
                  color: "#00D1C1",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: "8px 14px",
                  borderRadius: "999px",
                  border: "1px solid rgba(0,209,193,0.45)",
                  background: "linear-gradient(135deg, rgba(0,209,193,0.12) 0%, rgba(0,168,155,0.06) 100%)",
                  boxShadow: "0 0 0 3px rgba(0,209,193,0.08), 0 4px 14px rgba(0,209,193,0.18)",
                  alignSelf: "flex-start",
                }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M12 2l1.8 6.2L20 10l-6.2 1.8L12 18l-1.8-6.2L4 10l6.2-1.8L12 2z" />
                </svg>
                Managed Services
              </Link>
              <a
                href="/login"
                onClick={(e) => {
                  e.preventDefault();
                  handleMenuNavigation("/login", false);
                }}
                className="text-white/70 hover:text-white transition-colors"
              >
                Sign In
              </a>
              <a
                href="/book-demo"
                className="transition-colors text-center"
                style={{ color: "#00D1C1" }}
                onMouseEnter={(e) => (e.target.style.color = "#00E0CE")}
                onMouseLeave={(e) => (e.target.style.color = "#00D1C1")}
                onClick={(e) => {
                  e.preventDefault();
                  handleMenuNavigation("/book-demo", false);
                }}
              >
                Demo
              </a>
              <a
                href="/register"
                className="px-4 py-2 rounded-lg font-semibold text-center transition-colors"
                style={{
                  background:
                    "linear-gradient(108.58deg, #00D1C1 0%, #00A89B 100%)",
                  color: "#000000",
                }}
                onClick={(e) => {
                  e.preventDefault();
                  handleMenuNavigation("/register", false);
                }}
              >
                Free 5-Day Trial
              </a>
            </div>
          </motion.div>
        )}
      </div>
    </nav>
  );
};

export default PublicHeader;
